import React, { CSSProperties, forwardRef, cloneElement, ReactText, ReactElement, Fragment } from 'react';
import { useLayer, Placement, LayerSide, Arrow, mergeRefs } from 'react-laag';

type Position = Exclude<LayerSide, 'center'>;

const positionMap: Record<Position, Placement> = {
  top: 'top-center',
  bottom: 'bottom-center',
  left: 'left-center',
  right: 'right-center',
};

export interface ITooltip {
  // children: ReactNode[];
  children: ReactText | ReactElement;
  position?: Position;
  text: string;
  isOpen: boolean;
}

const Tooltip = forwardRef<any, ITooltip>(function Tooltip({ children, text, position = 'left', isOpen }, ref) {
  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen,
    placement: positionMap[position],
    possiblePlacements: ['top-center', 'bottom-center', 'left-center', 'right-center'],
    triggerOffset: 20,
  });

  let trigger: ReactElement;
  if (isReactText(children)) {
    // @ts-ignore
    trigger = <TooltipText ref={mergeRefs(ref, triggerProps.ref)}>{children}</TooltipText>;
  } else {
    trigger = cloneElement(children, {
      ref: mergeRefs(ref, triggerProps.ref),
    });
  }

  return (
    <Fragment>
      {trigger}
      {isOpen &&
        renderLayer(
          <TooltipBox {...layerProps}>
            {text}
            <Arrow
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              {...arrowProps}
              backgroundColor={BG_COLOR}
              borderColor={BORDER_COLOR}
              borderWidth={1}
              size={6}
            />
          </TooltipBox>
        )}
    </Fragment>
  );
});

function isReactText(children: React.ReactNode): children is React.ReactText {
  return ['string', 'number'].includes(typeof children);
}

export const BG_COLOR = '#6C6C6C';
export const BORDER_COLOR = '#6C6C6C';

const TooltipBox: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<{ readonly children?: any; readonly style?: CSSProperties }>
  // eslint-disable-next-line react/prop-types
> = forwardRef(function TooltipBox({ children, style: outerStyle }, ref) {
  const style: CSSProperties = {
    ...outerStyle,
    color: 'white',
    backgroundColor: 'rgba(0, 8, 46, 0.50)',
    backdropFilter: 'blur(3px)',
    width: '270px',
    border: '1px solid rgba(255, 255, 255, 0.20)',
    borderRadius: '9px',
    fontWeight: '400',
    lineHeight: '18px',
    fontSize: '14px',
    padding: '8px 16px',
  };

  return (
    <div
      // @ts-ignore
      ref={ref}
      style={style}
    >
      {children}
    </div>
  );
});

const TooltipText: React.ForwardRefExoticComponent<React.PropsWithoutRef<{ readonly children?: any }>> = forwardRef(
  // eslint-disable-next-line react/prop-types
  function TooltipText({ children }, ref) {
    return (
      <span
        // @ts-ignore
        ref={ref}
        style={{
          borderBottom: '1px dotted #1a73a7',
          color: '#1a73a7',
        }}
      >
        {children}
      </span>
    );
  }
);

export default Tooltip;
